import { useQuery } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../hooks/useAuth';
import type { OrgModelProjection } from '../models/OrgModelProjection';
import { orgModelQueryKey } from './orgModelQueryKeys';

const MAX_TEMPORAL_REFETCH_DELAY_MS = 2_147_000_000;

export function useOrgModel(input: {
  folderId: string | null | undefined;
  diagramId?: string | null;
  asOf?: string;
  txAt?: string;
  expectedGraphVersion?: number;
  maxRecords?: number;
  enabled?: boolean;
}) {
  const authenticatedFetch = useAuthenticatedFetch();
  return useQuery({
    queryKey: orgModelQueryKey(input),
    enabled: Boolean(input.folderId) && (input.enabled ?? true),
    // Canonical graph and access-policy events explicitly invalidate or purge
    // this permissioned projection. Treat it as current between those durable
    // signals so focus changes cannot rebuild the same model automatically.
    staleTime: Infinity,
    refetchOnWindowFocus: (query) => input.asOf
      ? false
      : temporalBoundaryHasElapsed(
          (query.state.data as OrgModelProjection | undefined)?.nextTemporalBoundary,
        ),
    refetchInterval: (query) => input.asOf
      ? false
      : temporalBoundaryRefetchDelay(
          (query.state.data as OrgModelProjection | undefined)?.nextTemporalBoundary,
        ),
    queryFn: async (): Promise<OrgModelProjection> => {
      const params = new URLSearchParams();
      if (input.asOf) params.set('asOf', input.asOf);
      if (input.txAt) params.set('txAt', input.txAt);
      if (input.expectedGraphVersion != null) params.set('expectedGraphVersion', String(input.expectedGraphVersion));
      if (input.maxRecords != null) params.set('maxRecords', String(input.maxRecords));
      if (input.diagramId) params.set('diagramId', input.diagramId);
      const suffix = params.size ? `?${params}` : '';
      const response = await authenticatedFetch(`/api/folders/${input.folderId}/atlas/org-model${suffix}`);
      if (!response.ok) {
        const error = new Error(`Failed to load org model (${response.status})`);
        (error as Error & { status?: number }).status = response.status;
        throw error;
      }
      return response.json() as Promise<OrgModelProjection>;
    },
  });
}

export function temporalBoundaryRefetchDelay(
  boundary: string | null | undefined,
  now = Date.now(),
): number | false {
  if (!boundary) return false;
  const at = Date.parse(boundary);
  if (Number.isNaN(at) || at <= now) return false;
  // Cross the half-open valid-time boundary before asking for the new model.
  return Math.min(MAX_TEMPORAL_REFETCH_DELAY_MS, Math.max(25, at - now + 25));
}

export function temporalBoundaryHasElapsed(
  boundary: string | null | undefined,
  now = Date.now(),
): boolean {
  if (!boundary) return false;
  const at = Date.parse(boundary);
  return !Number.isNaN(at) && at <= now;
}
